<template>
  <div class="ak-table-container" :class="containerClasses">
    <table class="ak-table" :class="tableClasses">
      <thead v-if="showHead && visibleData.length && visibleColumns.length">
        <tr class="ak-table__thead-row" :class="{ 'is-checked': isAllChecked }">
          <th v-if="checkable" class="ak-table__cell--1">
            <template v-if="headerCheckable">
              <ak-checkbox
                :model-value="isAllChecked"
                :disabled="isAllUncheckable"
                @change="checkAll"
              />
            </template>
          </th>

          <th
            v-for="(column, index) in visibleColumns"
            :key="index"
            class="ak-table__header"
            :class="{
              'ak-table__header--sortable': column.sortable,
              px: column.headerSpacing,
              'text-capitalize' : column.capitalize
            }"
            :style="{ 'min-width': column.headingMinWidth }"
            @click.stop="sort(column)"
            :colspan="column.colSpan"
          >
            <ak-pack spacing="tiny">
              <ak-pack-item class="text-nowrap">
                {{ column.label }}
              </ak-pack-item>
              <ak-pack-item>
                <ak-icon
                  v-if="currentSortColumn.label === column.label"
                  class="block"
                  :type="isAsc ? 'arrow-up-c' : 'arrow-down-c'"
                  size="small"
                />
              </ak-pack-item>
            </ak-pack>
          </th>
        </tr>
      </thead>

      <tbody v-if="visibleData.length">
        <template
          v-for="(row, index) in visibleData"
          :key="customRowKey ? row[customRowKey] : index"
        >
          <tr
            :class="{ 'is-checked': isRowChecked(row) }"
            @click="rowClicked(row)"
          >
            <td v-if="checkable" class="ak-table__cell--1">
              <ak-checkbox
                :model-value="isRowChecked(row)"
                :disabled="!isRowCheckable(row)"
                @change="checkRow(row)"
              />
            </td>

            <slot v-if="$slots.default" :row="row" :index="index" />

            <template v-else>
              <ak-table-column
                v-for="column in columnsModel"
                :key="column.field"
                v-bind="column"
                internal
              >
                {{ getValueByPath(row, column.field) }}
              </ak-table-column>
            </template>
          </tr>
        </template>
      </tbody>

      <tbody v-else>
        <tr v-if="!isLoading">
          <td :colspan="columnCount">
            <template v-if="emptyState">
              <ak-empty-state
              :title="emptyState.title"
              :icon="emptyState.icon"
              :fa="emptyState.fa"
              :faLight="emptyState.faLight"
            >
              {{ emptyState.text }}
              <div v-if="emptyState.button" class="pt">
                <ak-button
                  color="primary"
                  size="small"
                  @click="emptyState.click"
                >
                  {{ emptyState.button }}
                </ak-button>
              </div>
            </ak-empty-state>
            </template>
            <template v-else>
              <ak-empty-state
              title="Sorry"
              icon="sad"
            >
              There is no data to display
            </ak-empty-state>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <template v-if="paginated && showPagination && totalModel > perPage">
      <ak-box
        narrow
        :class="{
          'bg-white sticky sticky--bottom': stickyPagination,
          bt: bordered || showRows,
        }"
      >
        <ak-pagination
          v-if="totalModel > perPage"
          v-model:current="newCurrentPage"
          :per-page="perPage"
          :total="totalModel"
          @change="pageChanged"
        />
      </ak-box>
    </template>

    <ak-loading
      v-model:active="isLoading"
      :is-full-page="false"
      :can-cancel="false"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import { getValueByPath, indexOf } from '../../utils/helpers'
import Checkbox from '../checkbox/Checkbox.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import Icon from '../icon/Icon.vue'
import Input from '../input/Input.vue'
import Box from '../box/Box.vue'
import Loading from '../loading/Loading.vue'
import Pagination from '../pagination/Pagination.vue'
import TableColumn from './TableColumn.vue'
import EmptyState from '../emptyState/EmptyState.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-table :columns="columns" :data="data" />
   */
  name: 'AkTable',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Checkbox.name]: Checkbox,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Icon.name]: Icon,
    [Input.name]: Input,
    [Pagination.name]: Pagination,
    [TableColumn.name]: TableColumn,
    [Box.name]: Box,
    [Loading.name]: Loading,
    [EmptyState.name]: EmptyState,
  },

  provide() {
    return {
      akTable: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Array}
     * @description This is the data that we want to display in the
     * table.
     */
    data: {
      type: Array,
      default: () => [],
    },

    /**
     * @type {Array}
     * @description These are the table columns that we want to output
     * each of the table rows will also use these to render each cell.
     */
    columns: {
      type: Array,
      default: () => [],
    },

    /**
     * @type {Boolean}
     * @description This is the loading state of the table if this is
     * set to true then we are going to show the loading state of the
     * table component.
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to display the hover class
     * of the table component.
     */
    hoverable: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Controls the spacing of the cells in the table
     */
    spacing: {
      type: String,
      validator: (o) => ['flush', 'tiny', 'small', 'medium'].indexOf(o) >= 0,
      default: 'small',
    },

    /**
     * @type {Boolean}
     * @description Inset the first cells to give a nicer space feel
     */
    inset: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to display the row lines
     * table.
     */
    showRows: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to display the bordered
     * class of the table component container.
     */
    bordered: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to display the bg-white
     * class or not.
     */
    background: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not the table rows are checkable
     * if they are this will cause the ak-checkboxes to render for each
     * of the rows.
     */
    checkable: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to display the ak-checkbox
     * in the table head to check or uncheck all rows at the same time.
     */
    headerCheckable: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Array}
     * @description This will hold all the data for the rows that have
     * been checked.
     */
    checkedRows: {
      type: Array,
      default: () => [],
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not to the visible data is chunked
     * to handle pagination on the local dataset, if this is true, the table
     * will expect the external data source to handle the pagination.
     */
    backendPagination: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Controls whether or not the table data is paginated, this
     * will cause the ak-pagination to be displayed if there is enough data or
     * if the table has backend pagination enabled.
     */
    paginated: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number}
     * @description This is the current page of the dataset that is displayed
     * when the data is paginated either locally or using external data
     * sources.
     */
    currentPage: {
      type: Number,
      default: 1,
    },

    /**
     * @type {Number|String}
     * @description Controls the number of items to display in the table if the
     * pagination is enabled.
     */
    perPage: {
      type: [Number, String],
      default: 20,
    },

    /**
     * @type {Boolean}
     * @description Sticks the pagination to the bottom of the scrollable container
     */
    stickyPagination: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Boolean}
     * @description
     */
    sortable: {
      type: Boolean,
      default: false,
    },
    headingWidth: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * @type {String|Array|Undefined}
     * @description
     */
    defaultSort: {
      type: [String, Array],
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Controls whether the table should handle the sorting or if
     * we should rely on the external data source to sort the dataset.
     */
    backendSorting: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description The default sort direction for the table component.
     */
    defaultSortDirection: {
      type: String,
      default: 'asc',
    },

    /**
     * @type {Boolean}
     * @description Controls whether the table should handle the filtering or if
     * we should rely on the external data source to filter the dataset.
     */
    backendFiltering: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Number|String}
     * @description The total number of rows in the data set that the
     * table component has
     */
    total: {
      type: [Number, String],
      default: 0,
    },

    /**
     * @type {Function}
     * @description Callback method to check if the row is checkable or not
     */
    isRowCheckable: {
      type: Function,
      default: () => true,
    },

    /**
     * @type {String}
     * @description Custom row key
     */
    customRowKey: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Hide table <thead></thead>
     */
    hideHead: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @description Provide empty state details for empty <tbody></tbody> slot
     */
    emptyState: {
      type: Object,
      default: () => {},
    },

    /**
     * @type {Boolean}
     * @description Controls if the icon is using a font awesome icon or not
     */
     fa: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Boolean}
     * @description Controls if the font awesome icon is light
     */
     faLight: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Number}
     * @description Detects if there are multiple sections for a column
     * and makes header span their width
     */
    colSpan: {
      type: Number,
      default: 1,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: [
    'update:columns',
    'update:data',
    'update:checkedRows',
    'update:currentPage',
    'update:loading',
    'sort',
    'check',
    'checkAll',
    'page',
    'rowClicked',
  ],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const lastCheckedRowIndex = ref(null)
    const newCurrentPage = ref(props.currentPage)
    const currentSortColumn = ref({})
    const filters = ref({})
    const firstTimeSort = ref(true)
    const isAsc = ref(true)
    const isTable = ref(true)
    const columnsArray = ref(props.columns || [])

    const isLoading = computed({
      get() {
        return props.loading
      },
      set(val) {
        emit('update:loading', val)
      },
    })

    /**
     * @type {Object}
     * @description A reactive object that will be the model for the table
     * columns
     */
    const columnsModel = computed({
      get() {
        return columnsArray.value
      },
      set(value) {
        emit('update:columns', value)
      },
    })

    const headerWidth = computed(() => {
      return {
        'min-width': `${props.headingWidth}${
          Number.isInteger(props.headingWidth) ? 'px' : ''
        }`,
      }
    })

    /**
     * @type {Object}
     * @description A reactive object that will be the model for the table
     * data
     */
    const dataModel = computed({
      get() {
        return props.data
      },
      set(value) {
        emit('update:data', value)
      },
    })

    /**
     * @type {Object}
     * @description A reactive object that will be the model for the table
     * checked rows
     */
    const checkedRowsModel = computed({
      get() {
        return props.checkedRows
      },
      set(value) {
        emit('update:checkedRows', value)
      },
    })

    /**
     * @type {Object}
     * @description A reactive object that will be the model for the table
     * total rows count
     */
    const totalModel = computed(() =>
      props.backendPagination ? props.total : dataModel.value.length
    )

    /**
     * @type {Object}
     * @decription A reactive object to contain the boolean value that determines if the
     * pagination should be displayed
     */
    const showPagination = computed(() => {
      if (totalModel.value > props.perPage) {
        return true
      }

      return false
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the container div of the
     * component.
     */
    const containerClasses = computed(() => {
      return {
        relative: true, // required for the loading state
        'bg-white': props.background,
        b: props.bordered,
        'bb-none': props.bordered && props.showRows,
      }
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the table element.
     */
    const tableClasses = computed(() => {
      return {
        'ak-table--rows': props.showRows,
        'ak-table--hover': props.hoverable && visibleData.value.length,
        [`ak-table--${props.spacing}`]: props.spacing,
        'ak-table--inset': props.inset,
      }
    })

    /**
     * @returns {Array}
     * @description Returns the data for the table, this will chunk the
     * data if pagination is enabled unless we are using backend pagination
     * then we will just let the data through in changed.
     */
    const visibleData = computed(() => {
      if (props.backendPagination || !props.paginated) {
        return dataModel.value
      }

      const currentPage = newCurrentPage.value
      const perPage = props.perPage

      if (dataModel.value.length <= perPage) {
        return dataModel.value
      } else {
        const start = (currentPage - 1) * perPage
        const end = parseInt(start, 10) + parseInt(perPage, 10)
        return dataModel.value.slice(start, end)
      }
    })

    /**
     * @returns {Array}
     * @description Returns the visible columns of the table
     */
    const visibleColumns = computed(() => {
      return columnsModel.value.filter((column) => {
        return column.visible || column.visible === undefined
      })
    })

    /**
     * @returns {Boolean}
     * @description Check if all rows in the page are checkable.
     */
    const isAllUncheckable = computed(() => {
      const validVisibleData = visibleData.value.filter((row) =>
        props.isRowCheckable(row)
      )

      return validVisibleData.length === 0
    })

    /**
     * @returns {Boolean}
     * @description Check if all rows in the page are checked.
     */
    const isAllChecked = computed(() => {
      const validVisibleData = visibleData.value.filter((row) =>
        props.isRowCheckable(row)
      )

      if (validVisibleData.length === 0) {
        return false
      }

      const isAllChecked = validVisibleData.some((currentVisibleRow) => {
        return (
          indexOf(
            checkedRowsModel.value,
            currentVisibleRow,
            props.customIsChecked
          ) < 0
        )
      })

      return !isAllChecked
    })

    /**
     * @returns {Number}
     * @description Returns the count of the columns in the table
     */
    const columnCount = computed(() => {
      let count = columnsModel.value.length
      count += props.checkable ? 1 : 0
      count += props.detailed && props.showDetailIcon ? 1 : 0
      return count
    })

    /**
     * @returns {Boolean}
     * @description Check if has any sortable column.
     */
    const hasSortablenewColumns = computed(() => {
      return columnsModel.value.some((column) => {
        return column.sortable
      })
    })

    const hasHeaderWidth = computed(() => {
      return columnsModel.value.some((column) => {
        return column.headingMinWidth
      })
    })

    const showHead = computed(() => {
      return !props.hideHead
    })

    const hasSpacing = computed(() => {
      return columnsModel.value.some((column) => {
        return column.headerSpacing
      })
    })

    /**
     * @param {Boolean} isAllChecked
     * @description Handles the checking and unchecking of all the visible
     * table rows in the component
     */
    const checkAll = (isAllChecked) => {
      visibleData.value.forEach((currentRow) => {
        removeCheckedRow(currentRow)

        if (isAllChecked) {
          if (props.isRowCheckable(currentRow)) {
            checkedRowsModel.value.push(currentRow)
          }
        }
      })

      emit('checkAll', checkedRowsModel.value)
      emit('update:checkedRows', checkedRowsModel.value)
    }

    /**
     * @param {Object} row
     * @description Handles the checking and unchecking of the rows in the
     * table data.
     */
    const checkRow = (row) => {
      if (!props.isRowCheckable(row)) return

      if (!isRowChecked(row)) {
        checkedRowsModel.value.push(row)
      } else {
        removeCheckedRow(row)
      }

      emit('check', row)
      emit('update:checkedRows', checkedRowsModel.value)
    }

    /**
     * @param {Object} row
     * @description Handles removing a row from the list of checked items
     * in the table data.
     */
    const removeCheckedRow = (row) => {
      const index = indexOf(checkedRowsModel.value, row, props.customIsChecked)

      if (index >= 0) {
        checkedRowsModel.value.splice(index, 1)
      }
    }

    /**
     * @param {Object}
     * @returns {Boolean}
     * @description Returns a boolean flag to tell if the row should be
     * checked or not, this will be used for the value of the checkbox
     * component in each row.
     */
    const isRowChecked = (row) => {
      return indexOf(checkedRowsModel.value, row, props.customIsChecked) >= 0
    }

    /**
     * @param {Number}
     * @description Emits an event when the pagination "change" event is
     * picked up.
     */
    const pageChanged = (page) => {
      newCurrentPage.value = page > 0 ? page : 1
      emit('page', newCurrentPage.value)
      emit('update:currentPage', newCurrentPage.value)
    }

    /**
     * @description Initial sorted column based on the default-sort prop.
     */
    const initSort = () => {
      if (!props.defaultSort) return
      let sortField = ''
      let sortDirection = props.defaultSortDirection

      if (Array.isArray(props.defaultSort)) {
        sortField = props.defaultSort[0]
        if (props.defaultSort[1]) {
          sortDirection = props.defaultSort[1]
        }
      } else {
        sortField = props.defaultSort
      }

      columnsModel.value.forEach((column) => {
        if (column.field === sortField) {
          isAsc.value = sortDirection.toLowerCase() !== 'desc'
          sort(column, true)
        }
      })
    }

    /**
     * @description Call initSort only first time (For example async data).
     */
    const checkSort = () => {
      if (columnsModel.value.length && firstTimeSort.value) {
        initSort()
        firstTimeSort.value = false
      } else if (columnsModel.value.length) {
        if (currentSortColumn.value.field) {
          for (let i = 0; i < columnsModel.value.length; i++) {
            if (columnsModel.value[i].field === currentSortColumn.value.field) {
              currentSortColumn.value = columnsModel.value[i]
              break
            }
          }
        }
      }
    }

    /**
     * @param {Object} column
     * @param {Boolean} updatingData
     * @description Handles the sorting of the data
     */
    const sort = (column, updatingData = false) => {
      if (!column || !column.sortable) return
      if (!props.sortable) return

      if (!updatingData) {
        isAsc.value =
          column.label === currentSortColumn.value.label
            ? !isAsc.value
            : props.defaultSortDirection.toLowerCase() !== 'desc'
      }

      if (props.backendSorting || !firstTimeSort.value) {
        emit('sort', column.field, isAsc.value ? 'asc' : 'desc')
      }

      if (!props.backendSorting) {
        dataModel.value = sortBy(
          dataModel.value,
          column.field,
          column.customSort,
          isAsc.value
        )
      }

      currentSortColumn.value = column
    }

    /**
     * @param {Array} array
     * @param {String} key
     * @param {Function} fn
     * @param {Boolean} isAsc
     * @description Sort an array by key without mutating original data.
     * Call the user sort function if it was passed.
     */
    const sortBy = (array, key, fn, isAsc) => {
      let sorted = []
      let data = Object.assign([], array)

      // Sorting without mutating original data
      if (fn && typeof fn === 'function') {
        sorted = data.sort((a, b) => fn(a, b, isAsc))
      } else {
        sorted = data.sort((a, b) => {
          // Get nested values from objects
          let newA = getValueByPath(a, key)
          let newB = getValueByPath(b, key)

          // sort boolean type
          if (typeof newA === 'boolean' && typeof newB === 'boolean') {
            return isAsc ? newA - newB : newB - newA
          }

          if (!newA && newA !== 0) return 1
          if (!newB && newB !== 0) return -1
          if (newA === newB) return 0

          newA = typeof newA === 'string' ? newA.toUpperCase() : newA
          newB = typeof newB === 'string' ? newB.toUpperCase() : newB

          return isAsc ? (newA > newB ? 1 : -1) : newA > newB ? -1 : 1
        })
      }

      return sorted
    }

    const rowClicked = (row) => {
      emit('rowClicked', row)
    }

    onMounted(() => {
      checkSort()
    })

    return {
      columnsModel,
      headerWidth,
      hasHeaderWidth,
      hasSpacing,
      dataModel,
      checkedRowsModel,
      showPagination,
      containerClasses,
      tableClasses,
      visibleData,
      visibleColumns,
      isAllUncheckable,
      isAllChecked,
      columnCount,
      hasSortablenewColumns,
      showHead,
      checkAll,
      checkRow,
      removeCheckedRow,
      isRowChecked,
      pageChanged,
      initSort,
      checkSort,
      sort,
      sortBy,
      getValueByPath,
      lastCheckedRowIndex,
      totalModel,
      newCurrentPage,
      currentSortColumn,
      filters,
      firstTimeSort,
      isAsc,
      isTable,
      isLoading,
      rowClicked,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-table {
  $self: &;
  display: table;
  width: 100%;

  thead,
  &__head {
    display: table-header-group;
  }
  tbody,
  &__body {
    display: table-row-group;
  }
  tfoot,
  &__foot {
    display: table-footer-group;
  }

  tr,
  &__row {
    display: table-row;
  }

  th,
  &__header {
    position: sticky;
    top: 0;
    background-color: white;
    box-shadow: inset 0px -0.5px 0px 0px $color-grey-lighter;
    z-index: 5;
    display: table-cell;
    color: $color-grey;
    transform: scale(0.999); // fix for sticky cells
    background-clip: padding-box;
  }
  &__header--sortable {
    cursor: pointer;
    &:hover {
      color: $color-primary;
    }
  }

  // Table cells & spacing
  td,
  &__cell {
    display: table-cell;
    background-clip: padding-box;
  }
  // cell shrink
  td.ak-table__cell--1,
  &__cell--1 {
    width: 1%;
  }

  // Table cells & spacing
  &--tiny {
    th,
    #{$self}__header,
    td,
    #{$self}__cell {
      padding: calc($spacing-tiny / 2);
    }
  }
  &--small {
    th,
    #{$self}__header,
    td,
    #{$self}__cell {
      padding: $spacing-tiny;
    }
  }
  &--medium {
    th,
    #{$self}__header,
    td,
    #{$self}__cell {
      padding: $spacing-small;
    }
  }

  // Table rows
  &--rows {
    th,
    #{$self}__header,
    td,
    #{$self}__cell {
      border-bottom: 1px solid $color-grey-lighter;
    }
  }

  // Table rows hover
  &--hover {
    #{$self}__row:hover,
    tr:not(.ak-table__thead-row):hover {
      th,
      #{$self}__header,
      td,
      #{$self}__cell {
        background-color: $color-grey-lightest;
      }
    }
  }

  // checked row
  .is-checked,
  .is-checked:hover {
    td,
    #{$self}__cell {
      background-color: rgba($color-primary, 0.15);
    }
  }

  // inset spacing
  &--inset {
    #{$self}__row,
    tr {
      > *:first-child {
        padding-left: $spacing;
      }
      > *:last-child {
        padding-right: $spacing;
      }
    }
  }
}
</style>
